import { createRef } from 'react';

import { StylesConfig } from 'react-select';

import { useClickOutside } from '@hsp/react-hooks';
import { containsElement } from '@hsp/utilities';

import { VirtualSelect } from 'components/select';
import { IClient } from 'types/api/client';
import { IClientProps } from 'types/components/client';
import { s } from 'utilities/strings';

import { useDisplayValue, useOnChange, useRetrieveClients } from './hooks';

const colorStyles = <AllowMultiple extends boolean = false>(): StylesConfig<IClient, AllowMultiple> => {
  return {
    multiValue: (styles, { data: { backgroundColor, color } }) => {
      return {
        ...styles,
        backgroundColor,
        color,
      };
    },
    multiValueLabel: (styles, { data: { color } }) => ({
      ...styles,
      color,
    }),
    multiValueRemove: (styles, { data: { backgroundColor, color } }) => ({
      ...styles,
      ':hover': {
        backgroundColor,
        color,
      },
      color,
    }),
  };
};

function Client<AllowMultiple extends boolean = false>({
  allowMultiple,
  className,
  colorize = false,
  disabled = false,
  onSelected,
  onClose,
  placeholder,
  selected,
  useExternal = false,
}: IClientProps<AllowMultiple>) {
  const { clients, isFetching, selectedClients } = useRetrieveClients({ selected, useExternal });
  const displayName = useDisplayValue();
  const handleSelected = useOnChange({ onSelected });
  const containerRef = createRef<HTMLDivElement>();

  useClickOutside(containerRef, (e) => {
    if (containsElement(containerRef.current, e.target as HTMLElement)) {
      return;
    }
    onClose?.();
  });

  return (
    <div ref={containerRef}>
      <VirtualSelect
        className={className}
        closeMenuOnSelect={!allowMultiple}
        getOptionLabel={displayName}
        getOptionValue={({ id }) => id}
        isClearable
        isDisabled={isFetching || disabled}
        isLoading={isFetching}
        isMulti={allowMultiple}
        onChange={handleSelected}
        options={clients}
        placeholder={placeholder ?? s(['Site.Select', 'Site.Client'], { plural: true })}
        styles={colorize ? colorStyles<AllowMultiple>() : undefined}
        value={selectedClients()}
      />
    </div>
  );
}
export default Client;
